import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { useRestaurant, useRestaurantMenu } from '../hooks/useAPI';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Skeleton } from '../components/ui/skeleton';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { StarRating, RestaurantRatings } from '../components/RestaurantRatings';
import { 
  ArrowLeft, Star, Clock, MapPin, Phone, 
  Plus, ShoppingCart, Flame, RefreshCw, MessageSquare, LogIn
} from 'lucide-react';

const RestaurantDetailPage = () => {
  const { id } = useParams();
  const { t, language } = useLanguage();
  const { addItem, setIsOpen, restaurantId } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { restaurant, isLoading: loadingRestaurant, refresh: refreshRestaurant } = useRestaurant(id);
  const { menuItems, isLoading: loadingMenu, refresh: refreshMenu } = useRestaurantMenu(id);
  const [activeCategory, setActiveCategory] = useState('all');

  const [openStatus, setOpenStatus] = useState(null);
  useEffect(() => {
    if (!id) return;
    const API_URL = process.env.REACT_APP_BACKEND_URL;
    fetch(`${API_URL}/api/restaurants/${id}/open-status`)
      .then(r => r.ok ? r.json() : null)
      .then(setOpenStatus)
      .catch(() => {});
  }, [id]);

  const closedBanner = openStatus && openStatus.is_open === false ? (
    openStatus.reason === 'platform'
      ? (language === 'pt'
          ? `Plataforma fechada neste momento.${openStatus.platform_hours_today ? ` Horário de hoje: ${openStatus.platform_hours_today}.` : ' Tenta mais tarde.'}`
          : `The platform is closed right now.${openStatus.platform_hours_today ? ` Today: ${openStatus.platform_hours_today}.` : ''}`)
      : (language === 'pt'
          ? `Restaurante fechado neste momento.${openStatus.restaurant_hours_today ? ` Horário de hoje: ${openStatus.restaurant_hours_today}.` : ''}`
          : `Restaurant closed right now.${openStatus.restaurant_hours_today ? ` Today: ${openStatus.restaurant_hours_today}.` : ''}`)
  ) : null;

  const loading = loadingRestaurant || loadingMenu;

  const categories = ['all', ...new Set(menuItems.map(item => item.category))];

  const filteredItems = activeCategory === 'all' 
    ? menuItems 
    : menuItems.filter(item => item.category === activeCategory);

  const handleAddToCart = (item) => {
    // Require authentication to add to cart
    if (!user) {
      toast.error(
        language === 'pt' 
          ? 'Faça login para adicionar itens ao carrinho'
          : 'Please login to add items to cart'
      );
      navigate('/login?redirect=' + encodeURIComponent(`/restaurants/${id}`));
      return;
    }

    // Check if adding from different restaurant
    if (restaurantId && restaurantId !== restaurant.restaurant_id) {
      toast.error(
        language === 'pt' 
          ? 'Você só pode pedir de um restaurante por vez. Limpe o carrinho primeiro.'
          : 'You can only order from one restaurant at a time. Clear your cart first.'
      );
      return;
    }

    addItem({
      item_id: item.item_id,
      name: item.name,
      price: item.price,
      restaurant_id: restaurant.restaurant_id,
      restaurant_name: restaurant.name
    });
    
    toast.success(
      language === 'pt' 
        ? `${item.name} adicionado ao carrinho`
        : `${item.name} added to cart`
    );
  };

  const handleRefresh = () => {
    refreshRestaurant();
    refreshMenu();
    toast.success(t('language') === 'pt' ? 'Dados atualizados!' : 'Data refreshed!');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FDFBF7]">
        <Skeleton className="h-64 w-full" />
        <div className="container mx-auto px-4 py-8">
          <Skeleton className="h-12 w-64 mb-4" />
          <Skeleton className="h-6 w-96 mb-8" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {Array(4).fill(0).map((_, i) => (
              <Skeleton key={i} className="h-32 rounded-2xl" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!restaurant) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-[#004E64] mb-4">Restaurant not found</h2>
          <Link to="/restaurants">
            <Button className="bg-[#FF6B35] hover:bg-[#E85D2A] rounded-full">
              Back to Restaurants
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      {/* Hero Image */}
      <div className="relative h-64 md:h-80">
        <img 
          src={restaurant.cover_image || restaurant.logo}
          alt={restaurant.name}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
        
        <div className="absolute top-4 left-4 right-4 flex justify-between">
          <Link to="/restaurants">
            <Button variant="ghost" className="bg-white/20 backdrop-blur-md text-white hover:bg-white/30 rounded-full">
              <ArrowLeft size={20} className="mr-2" />
              {t('restaurants')}
            </Button>
          </Link>
          <Button 
            variant="ghost" 
            className="bg-white/20 backdrop-blur-md text-white hover:bg-white/30 rounded-full"
            onClick={handleRefresh}
            data-testid="refresh-menu-button"
          >
            <RefreshCw size={18} className="mr-2" />
            {t('language') === 'pt' ? 'Atualizar' : 'Refresh'}
          </Button>
        </div>
        
        <div className="absolute bottom-4 left-4 right-4">
          <div className="flex items-center gap-3 mb-2">
            <img 
              src={restaurant.logo}
              alt={restaurant.name}
              className="w-16 h-16 rounded-2xl object-cover border-2 border-white"
            />
            <div>
              <h1 className="text-3xl font-bold text-white" data-testid="restaurant-name">
                {restaurant.name}
              </h1>
              <p className="text-white/80">{restaurant.cuisine_type}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Restaurant Info */}
      <div className="container mx-auto px-4 py-6">
        {closedBanner && (
          <div
            className="mb-5 flex items-start gap-3 rounded-2xl border border-amber-300 bg-amber-50 px-4 py-3"
            data-testid="restaurant-closed-banner"
          >
            <Clock size={18} className="mt-0.5 shrink-0 text-amber-600" />
            <div>
              <p className="font-semibold text-amber-900 text-sm">
                {language === 'pt' ? 'Fechado de momento' : 'Currently closed'}
              </p>
              <p className="text-amber-800 text-xs mt-0.5">{closedBanner}</p>
            </div>
          </div>
        )}
        <div className="flex flex-wrap items-center gap-4 mb-6">
          <div className="flex items-center gap-1 bg-yellow-50 px-3 py-1.5 rounded-full">
            <Star className="text-yellow-500 fill-yellow-500" size={18} />
            <span className="font-bold text-[#004E64]">{restaurant.rating}</span>
          </div>
          <div className="flex items-center gap-1 text-[#9FA2B4]">
            <Clock size={18} />
            <span>{restaurant.delivery_time}</span>
          </div>
          <div className="flex items-center gap-1 text-[#9FA2B4]">
            <span>{t('minOrder')}: €{restaurant.min_order.toFixed(2)}</span>
          </div>
          <div className="text-[#FF6B35] font-bold">
            {t('deliveryFrom')}
          </div>
        </div>

        <p className="text-[#9FA2B4] mb-6">{restaurant.description}</p>

        {restaurant.address && (
          <div className="flex items-center gap-2 text-[#9FA2B4] mb-2">
            <MapPin size={18} />
            {restaurant.address}
          </div>
        )}
        {restaurant.phone && (
          <div className="flex items-center gap-2 text-[#9FA2B4] mb-6">
            <Phone size={18} />
            {restaurant.phone}
          </div>
        )}

        {/* Menu and Ratings Tabs */}
        <Tabs defaultValue="menu" className="w-full">
          <TabsList className="w-full justify-start overflow-x-auto bg-transparent border-b border-[#004E64]/10 rounded-none h-auto p-0 mb-6">
            <TabsTrigger
              value="menu"
              className="px-4 py-3 data-[state=active]:bg-transparent data-[state=active]:text-[#FF6B35] data-[state=active]:border-b-2 data-[state=active]:border-[#FF6B35] rounded-none"
              data-testid="tab-menu"
            >
              {t('language') === 'pt' ? 'Menu' : 'Menu'}
            </TabsTrigger>
            <TabsTrigger
              value="ratings"
              className="px-4 py-3 data-[state=active]:bg-transparent data-[state=active]:text-[#FF6B35] data-[state=active]:border-b-2 data-[state=active]:border-[#FF6B35] rounded-none flex items-center gap-2"
              data-testid="tab-ratings"
            >
              <MessageSquare size={16} />
              {t('language') === 'pt' ? 'Avaliações' : 'Reviews'}
            </TabsTrigger>
          </TabsList>

          {/* Menu Tab */}
          <TabsContent value="menu">
            {/* Menu Categories */}
            <div className="flex gap-2 overflow-x-auto pb-4 mb-4">
              {categories.map(category => (
                <Button
                  key={category}
                  variant={activeCategory === category ? 'default' : 'outline'}
                  size="sm"
                  className={`rounded-full whitespace-nowrap ${
                    activeCategory === category 
                      ? 'bg-[#FF6B35] hover:bg-[#E85D2A]' 
                      : 'border-[#004E64]/10'
                  }`}
                  onClick={() => setActiveCategory(category)}
                  data-testid={`category-${category}`}
                >
                  {category === 'all' ? t('all') : category}
                </Button>
              ))}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {filteredItems.map(item => (
                <Card 
                  key={item.item_id}
                  className={`menu-item-card flex gap-4 ${item.is_available === false ? 'opacity-60' : ''}`}
                  data-testid={`menu-item-${item.item_id}`}
                >
                  {item.image && (
                    <img 
                      src={item.image}
                      alt={item.name}
                      className={`w-24 h-24 rounded-xl object-cover flex-shrink-0 ${item.is_available === false ? 'grayscale' : ''}`}
                    />
                  )}
                  <CardContent className="p-0 flex-1 flex flex-col">
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <div className="flex items-center gap-2">
                          <h3 className="font-bold text-[#004E64]">{item.name}</h3>
                          {item.is_available === false && (
                            <Badge className="bg-red-500 hover:bg-red-500" data-testid={`item-unavailable-${item.item_id}`}>
                              {language === 'pt' ? 'Indisponível' : 'Unavailable'}
                            </Badge>
                          )}
                          {item.is_available !== false && item.is_popular && (
                            <Badge className="bg-[#FF6B35] hover:bg-[#FF6B35] flex items-center gap-1">
                              <Flame size={12} />
                              {t('popular')}
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-[#9FA2B4] line-clamp-2">{item.description}</p>
                      </div>
                    </div>
                    <div className="flex items-center justify-between mt-auto pt-3">
                      <span className="text-xl font-bold text-[#FF6B35]">
                        €{item.price.toFixed(2)}
                      </span>
                      {user ? (
                        item.is_available === false ? (
                          <Button size="sm" disabled className="rounded-full bg-gray-300 text-gray-600 cursor-not-allowed">
                            {language === 'pt' ? 'Indisponível' : 'Unavailable'}
                          </Button>
                        ) : (
                        <Button
                          size="sm"
                          className="bg-[#FF6B35] hover:bg-[#E85D2A] rounded-full"
                          onClick={() => handleAddToCart(item)}
                          data-testid={`add-to-cart-${item.item_id}`}
                        >
                          <Plus size={18} className="mr-1" />
                          {t('addToCart')}
                        </Button>
                        )
                      ) : (
                        <Link to={`/login?redirect=${encodeURIComponent(`/restaurants/${id}`)}`}>
                          <Button
                            size="sm"
                            variant="outline"
                            className="rounded-full border-[#FF6B35] text-[#FF6B35] hover:bg-[#FF6B35] hover:text-white"
                            data-testid={`login-to-add-${item.item_id}`}
                          >
                            {language === 'pt' ? 'Entrar para pedir' : 'Login to order'}
                          </Button>
                        </Link>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* Ratings Tab */}
          <TabsContent value="ratings">
            <RestaurantRatings restaurantId={id} />
          </TabsContent>
        </Tabs>
      </div>

      {/* Floating Cart Button (Mobile) */}
      <div className="fixed bottom-4 right-4 md:hidden">
        <Button
          onClick={() => setIsOpen(true)}
          className="bg-[#FF6B35] hover:bg-[#E85D2A] rounded-full w-14 h-14 shadow-lg"
          data-testid="floating-cart-button"
        >
          <ShoppingCart size={24} />
        </Button>
      </div>
    </div>
  );
};

export default RestaurantDetailPage;
